/**
 * Context Store for hierarchical navigation.
 *
 * **READ VIEWPORT ONLY.** This store drives the global `ScopeSelector` and
 * attaches `X-Context-Scope` / `X-Context-Id` headers to outbound API requests.
 * It is the single source of truth for *which slice of data the user is
 * currently viewing*.
 *
 * It is NOT a write target. Create / mutate forms MUST hold their own target
 * state (use `OrgTargetPicker` from `@/components/scope/OrgTargetPicker`) and
 * send explicit `distributor` / `MSP` / `company` fields in the request
 * body. Reading `useContextStore` inside an `onSubmit` / `handleCreate` is a
 * bug and is flagged by the `scope/no-context-in-write` ESLint rule.
 *
 * See `src/features/admin/README-scope.md` for the full rule and inventory.
 *
 * Allows System Admins, MSP Distributors, and MSP Admins to switch viewing context between:
 * - All MSPs (system admin only)
 * - MSP Distributor scope
 * - Specific MSP/MSP
 * - Specific Company
 */

import { create } from "zustand";
import { persist } from "zustand/middleware";

import { isOrganizationId } from "@/lib/utils";

export type ContextScope =
  | "all"
  | "distributor"
  | "msp"
  | "company"
  | "default";

const SCOPES: readonly ContextScope[] = [
  "all",
  "distributor",
  "msp",
  "company",
  "default",
];

export interface ViewContext {
  scope: ContextScope;
  distributorId: string | null;
  distributorName: string | null;
  mspId: string | null;
  mspName: string | null;
  companyId: string | null;
  companyName: string | null;
}

interface ContextState extends ViewContext {
  setContext: (context: Partial<ViewContext>) => void;
  setDistributorContext: (
    distributorId: string,
    distributorName: string,
  ) => void;
  setMspContext: (mspId: string, mspName: string) => void;
  setCompanyContext: (
    companyId: string,
    companyName: string,
    mspId?: string,
    mspName?: string,
  ) => void;
  /** MSP = "All MSPs" but keep distributor (and ids) from current state — no list lookup. */
  narrowToDistributorScope: () => void;
  /** Company = "All Companies" but keep distributor + MSP from current state — no list lookup. */
  narrowToMspScope: () => void;
  setAllContext: () => void;
  clearContext: () => void;
  getDisplayName: () => string;
}

export const useContextStore = create<ContextState>()(
  persist(
    (set, get) => ({
      scope: "default",
      distributorId: null,
      distributorName: null,
      mspId: null,
      mspName: null,
      companyId: null,
      companyName: null,

      setContext: (context) => {
        set((state) => ({ ...state, ...context }));
      },

      setDistributorContext: (distributorId, distributorName) => {
        set({
          scope: "distributor",
          distributorId: String(distributorId),
          distributorName,
          mspId: null,
          mspName: null,
          companyId: null,
          companyName: null,
        });
      },

      setMspContext: (mspId, mspName) => {
        const prev = get();
        set({
          scope: "msp",
          mspId: String(mspId),
          mspName,
          // Keep distributor when drilling distributor → MSP (UI + optional future headers)
          distributorId: prev.distributorId ? String(prev.distributorId) : null,
          distributorName: prev.distributorName,
          companyId: null,
          companyName: null,
        });
      },

      setCompanyContext: (companyId, companyName, mspId, mspName) => {
        const prev = get();
        set({
          scope: "company",
          companyId: String(companyId),
          companyName,
          mspId:
            mspId != null
              ? String(mspId)
              : prev.mspId != null
                ? String(prev.mspId)
                : null,
          mspName: mspName || prev.mspName,
          distributorId: prev.distributorId ? String(prev.distributorId) : null,
          distributorName: prev.distributorName,
        });
      },

      narrowToDistributorScope: () => {
        const prev = get();
        if (!prev.distributorId) {
          set({
            scope: "all",
            distributorId: null,
            distributorName: null,
            mspId: null,
            mspName: null,
            companyId: null,
            companyName: null,
          });
          return;
        }
        set({
          scope: "distributor",
          distributorId: String(prev.distributorId),
          distributorName: prev.distributorName,
          mspId: null,
          mspName: null,
          companyId: null,
          companyName: null,
        });
      },

      narrowToMspScope: () => {
        const prev = get();
        if (!prev.mspId) {
          get().narrowToDistributorScope();
          return;
        }
        set({
          scope: "msp",
          mspId: String(prev.mspId),
          mspName: prev.mspName,
          distributorId: prev.distributorId ? String(prev.distributorId) : null,
          distributorName: prev.distributorName,
          companyId: null,
          companyName: null,
        });
      },

      setAllContext: () => {
        set({
          scope: "all",
          distributorId: null,
          distributorName: null,
          mspId: null,
          mspName: null,
          companyId: null,
          companyName: null,
        });
      },

      clearContext: () => {
        set({
          scope: "default",
          distributorId: null,
          distributorName: null,
          mspId: null,
          mspName: null,
          companyId: null,
          companyName: null,
        });
      },

      getDisplayName: () => {
        const state = get();
        if (state.scope === "all") {
          return "All MSPs";
        }
        if (state.scope === "distributor") {
          return state.distributorName || "Distributor";
        }
        if (state.scope === "msp") {
          return state.mspName || "MSP";
        }
        if (state.scope === "company") {
          return state.companyName || "Company";
        }
        return "";
      },
    }),
    {
      name: "everythingcloud-context",
      /*
       * A saved selection is read back on every load, so one that cannot be
       * real is dropped on the way in. A browser that kept the fixture tree an
       * earlier build shipped holds ids like `d-acme`: the request interceptor
       * will not send them, so every page quietly showed the wrong scope while
       * the bar still named the old one. A scope outside the five the server
       * accepts is dropped for the same reason.
       */
      merge: (persisted, current) => {
        const saved = (persisted ?? {}) as Partial<ViewContext>;
        const ids = [saved.distributorId, saved.mspId, saved.companyId];
        const sound =
          SCOPES.includes(saved.scope as ContextScope) &&
          ids.every((id) => id == null || isOrganizationId(String(id)));
        return sound ? { ...current, ...saved } : current;
      },
    },
  ),
);
